import { fusedGpsFromOdom } from "../utils/fused-path.js";
import { subscribeToSelector } from "./subscribe-to-selector.js";
import { selectAlignmentMatrix, selectGpsPositions, selectOdometryPositions, selectOdometryRotations, selectZeroReference } from "./app-selectors.js";
import { calcGpsCoords } from "gps-plus-slam-js";
import { mat4, vec3 } from "gl-matrix";
//#region ../src/state/store-subscribers.ts
/**
* Wire store state-change subscriptions to visualization dependencies.
*
* Uses subscribeToSelector to decompose the monolithic subscriber into
* focused, change-gated callbacks:
* 1. Alignment matrix changes → apply matrix + capture snapshots
* 2. GPS position changes → incremental event visualization + map overlay
* 3. Reference point changes → forward to map overlay
*
* Each subscription uses reference equality to detect changes — callbacks
* only fire when the selector output actually differs between dispatches.
*
* Each call creates **fresh subscriptions** — no manual reset needed between
* sessions. Call the returned unsubscribe function to tear down all.
*
* @param store — any object satisfying SubscribableStore (RecorderStore, replay store, mock)
* @param deps  — visualization dependencies (real singletons or test mocks)
* @returns An unsubscribe function that removes all listeners from the store.
*/
function wireStoreSubscribers(store, deps) {
	const _odomVec = vec3.create();
	const _alignedVec = vec3.create();
	const unsubs = [];
	unsubs.push(subscribeToSelector(store, selectAlignmentMatrix, (matrix) => {
		if (!matrix) return;
		deps.applyAlignmentMatrix(matrix);
		const state = store.getState();
		const odomPositions = selectOdometryPositions(state);
		if (odomPositions.length > 0) {
			const latestOdom = odomPositions[odomPositions.length - 1];
			const glMat = mat4.fromValues(...matrix);
			vec3.set(_odomVec, latestOdom[0], latestOdom[1], latestOdom[2]);
			vec3.transformMat4(_alignedVec, _odomVec, glMat);
			const snapshotPos = [
				_alignedVec[0],
				_alignedVec[1],
				_alignedVec[2]
			];
			deps.gpsEventVisualizer.addAlignmentSnapshot(snapshotPos);
			deps.onAlignmentSnapshot?.(snapshotPos);
			const zeroRef = selectZeroReference(state);
			if (zeroRef && deps.mapOverlay?.addAlignmentSnapshot) {
				const gps = calcGpsCoords(zeroRef, _alignedVec);
				deps.mapOverlay.addAlignmentSnapshot(gps.lat, gps.lon);
			}
		}
	}));
	unsubs.push(subscribeToSelector(store, selectGpsPositions, (gpsPositions, prevPositions) => {
		const prevCount = prevPositions?.length ?? 0;
		const state = store.getState();
		const zeroRef = selectZeroReference(state);
		if (zeroRef && !deps.gpsEventVisualizer.getZeroRef()) deps.gpsEventVisualizer.setZeroRef(zeroRef);
		const odomPositions = selectOdometryPositions(state);
		const odomRotations = selectOdometryRotations(state);
		for (let i = prevCount; i < gpsPositions.length; i++) {
			const gpsPoint = gpsPositions[i];
			const odomPos = odomPositions[i];
			if (gpsPoint && odomPos) {
				deps.gpsEventVisualizer.addGpsEvent(gpsPoint.coordinates, odomPos);
				deps.onNewGpsPosition?.(gpsPoint.coordinates);
				deps.onNewGpsLatLng?.(gpsPoint.latitude, gpsPoint.longitude);
				deps.mapOverlay?.addRawGpsPoint?.(gpsPoint.latitude, gpsPoint.longitude);
				const alignmentMatrix = selectAlignmentMatrix(state);
				const zero = selectZeroReference(state);
				if (alignmentMatrix && zero && deps.mapOverlay?.addFusedPoint) {
					const fusedGps = fusedGpsFromOdom(alignmentMatrix, odomPos, zero);
					deps.mapOverlay.addFusedPoint(fusedGps.lat, fusedGps.lon);
				}
				const odomRot = odomRotations[i];
				if (odomRot) deps.onNewOdomPose?.(odomPos, odomRot);
			}
		}
		if (deps.mapOverlay && gpsPositions.length > 0) {
			const lastGpsPoint = gpsPositions[gpsPositions.length - 1];
			deps.mapOverlay.setGpsPosition(lastGpsPoint.latitude, lastGpsPoint.longitude);
		}
	}));
	return () => {
		for (const unsub of unsubs) unsub();
	};
}
//#endregion
export { wireStoreSubscribers };
